/**
 * Atlas Data — Capital With Coordinates Intelligence System
 * GRANULLO_CAPITAL_WITH_COORDINATES_CANONICAL_BUILD_V1
 *
 * Comprehensive dataset for the living need + trust + deployability atlas.
 * Each region contains: need density, foundation nodes, urgency, deployability,
 * spend absorbability, proof maturity, and arrival pathway data.
 */

export const ATLAS_REGIONS = [
  {
    id: 'IN', iso: 'IND', name: 'India',
    center: [78.96, 20.59],
    needDensity: 0.87, urgency: 'critical', verifiedNodes: 14,
    deployETA: '2-4 weeks', railReady: true, proofMaturity: 'high',
    spendAbsorb: '$2M-$50M', trappedCapital: '$340M',
    causes: ['water', 'education', 'livelihoods', 'health', 'women'],
    activeNeed: 'Rural water access deficit across 6 states, affecting 23M people. 47% of CSR water spend lacks last-mile delivery proof.',
    foundations: ['Azim Premji Foundation', 'Tata Trusts', 'Infosys Foundation', 'Piramal Foundation'],
    missing: ['District-level monitoring', 'Last-mile payment verification', 'Real-time beneficiary confirmation'],
    arrivalTime: '72h verified corridors',
    proofPath: 'Real-time replay + beneficiary verification',
    needsNode: false,
    spotlight: {
      title: 'Rajasthan Water Resilience',
      story: '230,000 smallholder farmers in drought-prone Rajasthan need water infrastructure. $4.2M CSR budget deployed, but only 31% verified at beneficiary level.',
      spend: '$4.2M', beneficiaries: '230,000', gap: '69% unverified delivery',
    },
  },
  {
    id: 'KE', iso: 'KEN', name: 'Kenya',
    center: [37.91, -0.02],
    needDensity: 0.78, urgency: 'high', verifiedNodes: 8,
    deployETA: '1-3 weeks', railReady: true, proofMaturity: 'high',
    spendAbsorb: '$500K-$10M', trappedCapital: '$85M',
    causes: ['health', 'education', 'agriculture', 'women', 'energy'],
    activeNeed: 'Maternal health coverage gaps in 4 counties. Mobile money rails enable rapid disbursement but lack structural proof.',
    foundations: ['KPMG Foundation Kenya', 'Equity Group Foundation', 'Safaricom Foundation'],
    missing: ['County-level outcome tracking', 'Beneficiary identity verification at scale'],
    arrivalTime: '48h mobile money corridors',
    proofPath: 'Mobile verification + replay export',
    needsNode: false,
    spotlight: {
      title: 'Maternal Health — Turkana County',
      story: '42,000 expectant mothers lack access to prenatal care. Mobile health units funded but disbursement proof incomplete.',
      spend: '$1.8M', beneficiaries: '42,000', gap: '58% untracked disbursement',
    },
  },
  {
    id: 'NG', iso: 'NGA', name: 'Nigeria',
    center: [8.68, 9.08],
    needDensity: 0.82, urgency: 'critical', verifiedNodes: 6,
    deployETA: '3-6 weeks', railReady: false, proofMaturity: 'medium',
    spendAbsorb: '$1M-$20M', trappedCapital: '$520M',
    causes: ['education', 'health', 'livelihoods', 'housing', 'women'],
    activeNeed: 'Out-of-school children crisis: 18.5M children without access. Education budgets fragmented across 36 states.',
    foundations: ['Dangote Foundation', 'Tony Elumelu Foundation', 'MTN Foundation'],
    missing: ['State-level disbursement verification', 'Unified beneficiary registry', 'Rail standardization'],
    arrivalTime: '5-10 days for verified corridors',
    proofPath: 'Milestone-based verification',
    needsNode: false,
    spotlight: {
      title: 'Northern Education Emergency',
      story: '8.7M children in northern Nigeria without school access. $12M pledged by corporate sponsors, $4.2M trapped in approval cycles.',
      spend: '$12M', beneficiaries: '8,700,000', gap: '35% capital trapped',
    },
  },
  {
    id: 'BD', iso: 'BGD', name: 'Bangladesh',
    center: [90.36, 23.68],
    needDensity: 0.75, urgency: 'high', verifiedNodes: 5,
    deployETA: '2-4 weeks', railReady: true, proofMaturity: 'medium',
    spendAbsorb: '$500K-$5M', trappedCapital: '$62M',
    causes: ['climate', 'disaster', 'women', 'livelihoods', 'education'],
    activeNeed: 'Climate displacement affecting 6.7M people in coastal regions. Flood response capital chronically delayed.',
    foundations: ['BRAC', 'Grameen Foundation', 'ASA Foundation'],
    missing: ['Disaster-speed disbursement rails', 'Climate-linked proof automation'],
    arrivalTime: '24-72h emergency corridors',
    proofPath: 'Beneficiary verification + disaster replay',
    needsNode: false,
  },
  {
    id: 'BR', iso: 'BRA', name: 'Brazil',
    center: [-51.93, -14.24],
    needDensity: 0.62, urgency: 'moderate', verifiedNodes: 7,
    deployETA: '2-4 weeks', railReady: true, proofMaturity: 'high',
    spendAbsorb: '$1M-$25M', trappedCapital: '$180M',
    causes: ['climate', 'education', 'health', 'housing', 'livelihoods'],
    activeNeed: 'Amazon deforestation-linked community displacement. Urban favela health infrastructure gaps.',
    foundations: ['Instituto Natura', 'Fundacao Lemann', 'Itau Social'],
    missing: ['Remote area payment rails', 'Indigenous community verification'],
    arrivalTime: '3-7 days verified corridors',
    proofPath: 'Satellite + beneficiary verification',
    needsNode: false,
  },
  {
    id: 'ET', iso: 'ETH', name: 'Ethiopia',
    center: [40.49, 9.15],
    needDensity: 0.91, urgency: 'critical', verifiedNodes: 2,
    deployETA: '6-10 weeks', railReady: false, proofMaturity: 'low',
    spendAbsorb: '$250K-$5M', trappedCapital: '$410M',
    causes: ['disaster', 'health', 'agriculture', 'water', 'education'],
    activeNeed: 'Conflict-displaced populations: 4.2M people. Drought cycle intensifying. Capital arrives too late or not at all.',
    foundations: ['Ethiopian Red Cross Society', 'Agar Ethiopia'],
    missing: ['Trusted node network', 'Payment rail infrastructure', 'Real-time proof capability', 'Identity verification at scale'],
    arrivalTime: '2-4 weeks (limited corridors)',
    proofPath: 'Manual verification — no automated replay',
    needsNode: true,
  },
  {
    id: 'CD', iso: 'COD', name: 'DR Congo',
    center: [21.76, -4.04],
    needDensity: 0.94, urgency: 'critical', verifiedNodes: 1,
    deployETA: '8-14 weeks', railReady: false, proofMaturity: 'low',
    spendAbsorb: '$100K-$2M', trappedCapital: '$680M',
    causes: ['disaster', 'health', 'water', 'education', 'women'],
    activeNeed: 'Multi-crisis: conflict, disease outbreaks, infrastructure collapse. $680M in pledged capital cannot reach beneficiaries.',
    foundations: ['Caritas Congo'],
    missing: ['Trusted node network at scale', 'Payment infrastructure', 'Identity systems', 'Proof architecture', 'Last-mile delivery'],
    arrivalTime: 'Unknown — no verified corridors',
    proofPath: 'None currently available',
    needsNode: true,
  },
  {
    id: 'ID', iso: 'IDN', name: 'Indonesia',
    center: [113.92, -0.79],
    needDensity: 0.68, urgency: 'moderate', verifiedNodes: 6,
    deployETA: '2-4 weeks', railReady: true, proofMaturity: 'medium',
    spendAbsorb: '$500K-$15M', trappedCapital: '$95M',
    causes: ['climate', 'education', 'disaster', 'health', 'livelihoods'],
    activeNeed: 'Island community isolation. Climate vulnerability across 17,000 islands. Education access disparity.',
    foundations: ['Tanoto Foundation', 'Bakti Foundation', 'Dompet Dhuafa'],
    missing: ['Inter-island payment rails', 'Remote verification systems'],
    arrivalTime: '3-7 days main corridors',
    proofPath: 'Digital verification + GPS proof',
    needsNode: false,
  },
  {
    id: 'PH', iso: 'PHL', name: 'Philippines',
    center: [121.77, 12.88],
    needDensity: 0.71, urgency: 'high', verifiedNodes: 5,
    deployETA: '1-3 weeks', railReady: true, proofMaturity: 'medium',
    spendAbsorb: '$250K-$8M', trappedCapital: '$72M',
    causes: ['disaster', 'education', 'health', 'livelihoods', 'housing'],
    activeNeed: 'Typhoon corridor: 20+ major events annually. Disaster capital response averaging 14 days too slow.',
    foundations: ['Ayala Foundation', 'SM Foundation', 'Philippine Disaster Resilience Foundation'],
    missing: ['Pre-positioned disaster capital', 'Automated disaster-trigger disbursement'],
    arrivalTime: '24-48h disaster corridors',
    proofPath: 'Disaster replay + mobile verification',
    needsNode: false,
  },
  {
    id: 'PK', iso: 'PAK', name: 'Pakistan',
    center: [69.35, 30.38],
    needDensity: 0.80, urgency: 'critical', verifiedNodes: 4,
    deployETA: '3-6 weeks', railReady: false, proofMaturity: 'low',
    spendAbsorb: '$500K-$10M', trappedCapital: '$290M',
    causes: ['disaster', 'education', 'health', 'water', 'women'],
    activeNeed: 'Post-flood recovery: 33M affected. Education system collapse in Sindh and Balochistan.',
    foundations: ['Aga Khan Foundation', 'Citizens Foundation', 'Edhi Foundation'],
    missing: ['Provincial disbursement rails', 'Flood-resilient proof systems', 'Female beneficiary verification'],
    arrivalTime: '7-14 days limited corridors',
    proofPath: 'Manual + partial digital verification',
    needsNode: true,
  },
  {
    id: 'MM', iso: 'MMR', name: 'Myanmar',
    center: [95.96, 21.91],
    needDensity: 0.88, urgency: 'critical', verifiedNodes: 0,
    deployETA: 'Unknown', railReady: false, proofMaturity: 'none',
    spendAbsorb: '<$500K', trappedCapital: '$450M',
    causes: ['disaster', 'health', 'education', 'women', 'housing'],
    activeNeed: 'Humanitarian crisis: 17.6M in need. No functional payment rails. Capital completely trapped.',
    foundations: [],
    missing: ['Everything: nodes, rails, proof, identity, delivery infrastructure'],
    arrivalTime: 'No verified corridors exist',
    proofPath: 'None available',
    needsNode: true,
  },
  {
    id: 'CO', iso: 'COL', name: 'Colombia',
    center: [-74.30, 4.57],
    needDensity: 0.58, urgency: 'moderate', verifiedNodes: 5,
    deployETA: '2-3 weeks', railReady: true, proofMaturity: 'medium',
    spendAbsorb: '$250K-$8M', trappedCapital: '$48M',
    causes: ['livelihoods', 'education', 'health', 'housing', 'women'],
    activeNeed: 'Venezuelan migrant integration. Rural-urban displacement. Coffee farmer livelihood transition.',
    foundations: ['Fundacion Corona', 'Fundacion Luker', 'ProBogota'],
    missing: ['Migrant-specific identity verification', 'Cross-border proof continuity'],
    arrivalTime: '3-5 days verified corridors',
    proofPath: 'Digital verification + community validation',
    needsNode: false,
  },
  {
    id: 'TZ', iso: 'TZA', name: 'Tanzania',
    center: [34.89, -6.37],
    needDensity: 0.73, urgency: 'high', verifiedNodes: 4,
    deployETA: '2-4 weeks', railReady: true, proofMaturity: 'medium',
    spendAbsorb: '$250K-$5M', trappedCapital: '$55M',
    causes: ['education', 'health', 'water', 'agriculture', 'women'],
    activeNeed: 'Secondary education dropout crisis: 2.1M girls. Agricultural productivity gaps in 5 regions.',
    foundations: ['Foundation for Civil Society', 'CCBRT Foundation', 'Vodacom Foundation TZ'],
    missing: ['Girls-education proof framework', 'Agricultural output verification'],
    arrivalTime: '3-7 days mobile corridors',
    proofPath: 'Mobile money verification + attendance proof',
    needsNode: false,
  },
  {
    id: 'GT', iso: 'GTM', name: 'Guatemala',
    center: [-90.23, 15.78],
    needDensity: 0.69, urgency: 'high', verifiedNodes: 2,
    deployETA: '4-8 weeks', railReady: false, proofMaturity: 'low',
    spendAbsorb: '$100K-$2M', trappedCapital: '$38M',
    causes: ['health', 'education', 'agriculture', 'women', 'water'],
    activeNeed: 'Child malnutrition emergency in highland communities. Indigenous community exclusion from formal systems.',
    foundations: ['Fundacion Carlos Slim Guatemala', 'FUNDESA'],
    missing: ['Indigenous language identity systems', 'Rural payment rails', 'Community-level proof'],
    arrivalTime: '7-14 days limited corridors',
    proofPath: 'Manual verification only',
    needsNode: true,
  },
  {
    id: 'MZ', iso: 'MOZ', name: 'Mozambique',
    center: [35.53, -18.67],
    needDensity: 0.77, urgency: 'high', verifiedNodes: 2,
    deployETA: '4-8 weeks', railReady: false, proofMaturity: 'low',
    spendAbsorb: '$100K-$3M', trappedCapital: '$120M',
    causes: ['disaster', 'health', 'education', 'climate', 'water'],
    activeNeed: 'Cyclone corridor: Idai/Kenneth recovery incomplete. Northern insurgency displacement. Climate vulnerability acute.',
    foundations: ['FDC Mozambique', 'Aga Khan Foundation MZ'],
    missing: ['Post-disaster payment infrastructure', 'Conflict-zone verification', 'Climate-resilient proof systems'],
    arrivalTime: '10-21 days limited access',
    proofPath: 'Partial manual verification',
    needsNode: true,
  },
];

export const CAUSES_TAXONOMY = [
  { id: 'water', label: 'Water & Sanitation', icon: 'droplet' },
  { id: 'education', label: 'Education', icon: 'book' },
  { id: 'health', label: 'Healthcare', icon: 'heart' },
  { id: 'climate', label: 'Climate Resilience', icon: 'leaf' },
  { id: 'agriculture', label: 'Agriculture & Livelihoods', icon: 'wheat' },
  { id: 'livelihoods', label: 'Livelihoods', icon: 'briefcase' },
  { id: 'women', label: 'Women & Gender', icon: 'users' },
  { id: 'disaster', label: 'Disaster Response', icon: 'alert-triangle' },
  { id: 'housing', label: 'Housing & Shelter', icon: 'home' },
  { id: 'energy', label: 'Clean Energy', icon: 'zap' },
];

export const BUDGET_RANGES = [
  { id: 'micro', label: '<$50K', min: 0, max: 50000 },
  { id: 'small', label: '$50K-$250K', min: 50000, max: 250000 },
  { id: 'medium', label: '$250K-$1M', min: 250000, max: 1000000 },
  { id: 'large', label: '$1M-$10M', min: 1000000, max: 10000000 },
  { id: 'major', label: '$10M+', min: 10000000, max: Infinity },
];

export const URGENCY_LEVELS = [
  { id: 'immediate', label: 'Immediate', desc: 'Deploy within days' },
  { id: 'quarter', label: 'This Quarter', desc: 'Within 3 months' },
  { id: 'annual', label: '12 Months', desc: 'Within the year' },
  { id: 'multi_year', label: 'Multi-Year', desc: 'Sustained deployment' },
];

export const PROOF_METHODS = [
  { id: 'instant_arrival', label: 'Instant Arrival Proof', desc: 'Real-time confirmation of capital reaching beneficiary' },
  { id: 'replay_export', label: 'Replay Export', desc: 'Full transaction replay for audit and evidence' },
  { id: 'board_reporting', label: 'Board Reporting', desc: 'Structured reports for governance and compliance' },
  { id: 'sdg_evidence', label: 'SDG Evidence', desc: 'Sustainable Development Goal alignment proof' },
  { id: 'beneficiary_verification', label: 'Beneficiary Verification', desc: 'Identity-verified delivery confirmation' },
];

/** Need density color for map (monochrome with scarlet for critical/trapped) */
export function getNeedColor(region) {
  if (region.needsNode || region.trappedCapital && parseTrappedAmount(region.trappedCapital) > 400_000_000) {
    return 'var(--shell-scarlet, #CD2200)';
  }
  const d = region.needDensity;
  if (d > 0.85) return 'rgba(205, 34, 0, 0.35)';
  if (d > 0.7) return 'rgba(10, 10, 10, 0.55)';
  if (d > 0.5) return 'rgba(10, 10, 10, 0.35)';
  return 'rgba(10, 10, 10, 0.18)';
}

function parseTrappedAmount(str) {
  if (!str) return 0;
  const m = str.replace(/[^0-9.]/g, '');
  return parseFloat(m) * 1_000_000 || 0;
}

/** Get urgency pulse rate (ms) */
export function getUrgencyPulse(urgency) {
  switch (urgency) {
    case 'critical': return 1200;
    case 'high': return 2000;
    case 'moderate': return 3500;
    default: return 5000;
  }
}
